import { useState } from 'react';
import { AlertTriangle, RotateCcw, X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getAssessmentV2ResultLabel,
  isAssessmentV2ReportKey,
} from '../registry/assessmentRegistry';
import type { AssessmentResultV2 } from '../types';
import { getAssessmentResultIdentifier } from '../utils/patientIdentifier';

interface AssessmentResultStatusModalProps {
  result: AssessmentResultV2;
  mode: 'invalidate' | 'restore';
  onClose: () => void;
  onConfirm: (resultId: string, justification: string) => Promise<void>;
}

export default function AssessmentResultStatusModal({
  result,
  mode,
  onClose,
  onConfirm,
}: AssessmentResultStatusModalProps) {
  const [justification, setJustification] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isInvalidate = mode === 'invalidate';
  const isReport = isAssessmentV2ReportKey(result.testKey);
  const resourceSubject = isReport ? 'O documento' : 'A avaliação';
  const title = isInvalidate
    ? `Invalidar ${isReport ? 'Documento' : 'Avaliação'}`
    : `Restaurar ${isReport ? 'Documento' : 'Avaliação'}`;
  const actionLabel = isInvalidate ? 'invalidar' : 'restaurar';
  const buttonLabel = isInvalidate ? 'Confirmar Invalidação' : 'Confirmar Restauração';
  const accentClasses = isInvalidate
    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    : 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500';
  const icon = isInvalidate ? (
    <AlertTriangle className="h-5 w-5 text-red-600" />
  ) : (
    <RotateCcw className="h-5 w-5 text-emerald-600" />
  );
  const assessmentDate = new Date(result.assessmentDate);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!justification.trim()) {
      toast.error('A justificativa é obrigatória.');
      return;
    }

    if (justification.trim().length < 10) {
      toast.error('A justificativa deve ter pelo menos 10 caracteres.');
      return;
    }

    if (!confirmChecked) {
      toast.error('Confirme a ação antes de prosseguir.');
      return;
    }

    try {
      setSubmitting(true);
      await onConfirm(result.resultId, justification.trim());
      onClose();
    } catch (error) {
      console.error(`Erro ao ${actionLabel} avaliação V2:`, error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className={`rounded-full p-2 ${isInvalidate ? 'bg-red-100' : 'bg-emerald-100'}`}>{icon}</div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">
                {isInvalidate
                  ? `${resourceSubject} será ${isReport ? 'ocultado' : 'ocultada'} da visualização padrão, mas continuará ${isReport ? 'salvo' : 'salva'} para auditoria.`
                  : `${resourceSubject} voltará a aparecer na visualização padrão e manterá o histórico de auditoria.`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={submitting}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className={`rounded-xl border p-4 text-sm ${isInvalidate ? 'border-red-200 bg-red-50 text-red-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
            {isInvalidate
              ? `${resourceSubject} deixará de aparecer nas listagens padrão e no fluxo assistencial corrente.`
              : `${resourceSubject} será ${isReport ? 'restaurado' : 'restaurada'} e voltará a aparecer normalmente nas listagens padrão.`}
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <div className="mb-3 text-sm font-semibold text-gray-900">Resumo da avaliação</div>
            <div className="space-y-2">
              <div>
                <span className="font-medium text-gray-600">
                  {isReport ? 'Documento:' : 'Teste:'}
                </span>{' '}
                {getAssessmentV2ResultLabel(result)}
              </div>
              <div>
                <span className="font-medium text-gray-600">Paciente:</span> {result.patientNameSnapshot || 'Paciente não identificado'}
              </div>
              <div>
                <span className="font-medium text-gray-600">Identificação:</span> {getAssessmentResultIdentifier(result) || 'Sem identificador'}
              </div>
              <div>
                <span className="font-medium text-gray-600">Data:</span>{' '}
                {Number.isNaN(assessmentDate.getTime()) ? '—' : assessmentDate.toLocaleString('pt-BR')}
              </div>
              <div>
                <span className="font-medium text-gray-600">Profissional:</span> {result.professionalNameSnapshot || '—'}
              </div>
            </div>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Justificativa
                <span className="ml-2 text-xs text-gray-500">(mínimo 10 caracteres)</span>
              </label>
              <textarea
                rows={4}
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder={`Descreva o motivo para ${actionLabel} ${isReport ? 'este documento' : 'esta avaliação'}...`}
                minLength={10}
                disabled={submitting}
                required
              />
              <div className="mt-1 text-xs text-gray-500">Caracteres: {justification.length}</div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(event) => setConfirmChecked(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
                disabled={submitting}
              />
              <span>
                Confirmo que desejo {actionLabel} {isReport ? 'este documento' : 'esta avaliação'} e
                registrar esta ação no histórico de auditoria.
              </span>
            </label>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting || !confirmChecked || justification.trim().length < 10}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 ${accentClasses}`}
              >
                {submitting ? 'Salvando...' : buttonLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
